import { Injectable } from '@angular/core';
import { APIService } from './api.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Grouping } from 'app/classes/grouping';
import { Org } from 'app/classes/org';
import { Asset } from "app/classes/asset";
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OrgService {

  API_URL = 'https://q3167hk0p0.execute-api.eu-west-2.amazonaws.com/4d/';

  constructor(private apiService: APIService, private http: HttpClient) { }


  updateSettings(orgId: number, settings: any[]) {

    const params = new HttpParams().set('a', 'update-changed').set('id', orgId ?? null);
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    return firstValueFrom(this.http
      .post<any[]>(this.API_URL + 'settings', settings, options))
      .then((response) => {
        return response;
      });


  }

  getSettings(orgId?: number): Promise<any> {
    const params = new HttpParams().set('a', 'get-all').set('id', orgId ?? null);
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    return firstValueFrom(this.http
      .get<any[]>(this.API_URL + 'settings', options))
      .then((response) => {
        return response;
      });
  }

  getOrgsAvailable(): Promise<Org[]> {
    return new Promise(resolve => {
      const url = `${this.API_URL}access`;
      const headers = this.apiService.getUAOHeaders();
      if (!headers) {
        console.info('No header');
        return;
      }
      try {
        return this.http
          .get<any[]>(url, this.apiService.getUAOHeaders())
          .subscribe(b => resolve(b.map(o => new Org(o))));
      } catch (e) {
        console.error(e);
      }
    });
  }

  getDefaultSensors(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.get('a=get-default-sensors')
        .then(response => {
          resolve(response);
        });
    });
  }

  updateDefaultSensors(settings: any) {
    return new Promise((resolve, reject) => {
      this.post(settings, 'a=update-default-sensors')
        .then(response => {
          resolve(response);
        });
    });
  }

  async getGroupings(): Promise<Grouping[]> {
    return new Promise(async (resolve) => {
      this.get('a=get-groupings')
        .then(response => {
          resolve(response.map(r => new Grouping(r)));
        });
    });
  }

  async getGrouping(groupingId: number): Promise<Grouping> {
    return new Promise(async (resolve) => {
      this.get(`a=get-grouping&i=${groupingId}`)
        .then(r => {
          resolve(new Grouping(r));
        });
    });
  }

  async getGroupingsAndUsers(): Promise<Grouping[]> {
    return new Promise(async (resolve) => {
      this.get(`a=get-groupings-and-users`)
        .then(r => {
          resolve(r.map(r => new Grouping(r)));
        });
    });
  }

  async updateGrouping(grouping: Grouping): Promise<any> {
    return new Promise(async (resolve) => {
      this.post({ grouping: grouping.serialise() }, 'a=update-grouping')
        .then(response => {
          resolve(response);
        });
    });
  }

  async getTelemetry(filter?: any): Promise<IGetOrgTelemetryResponse[]> {
    return new Promise(resolve => {
      if (filter) {
        filter = encodeURIComponent(JSON.stringify(filter));
      }

      const url = `${this.API_URL}telemetry${filter ? `?filter=${filter}` : ''}`;
      return this.http
        .get<IGetOrgTelemetryResponse[]>(url, this.apiService.getUAOHeaders())
        .subscribe(b => {
          b.forEach(a => a.formattedValue = Asset.formattedValue(a.value, a.assetType, a.purposeId, a.updatedAt, a.identifier),)
          resolve(b);
        });
    });
  }

  async getOrgUserActivity(view: 'org' | 'all'): Promise<{ users: any[], urls: any[], orgs: any[] }> {
    return new Promise(resolve => {
      const url = `${this.API_URL}activity?v=${view}`;
      return this.http
        .get<any>(url, this.apiService.getUAOHeaders())
        .subscribe(b => resolve(b));
    });
  }

  post(body, qs: string = ''): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `${this.API_URL}?${qs}`;
      return this.http
        .post<any>(url, body, this.apiService.getUAOHeaders())
        .subscribe(b => resolve(b));
    });
  }

  get(qs: string = ''): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `${this.API_URL}?${qs}`;
      return this.http
        .get<any>(url, this.apiService.getUAOHeaders())
        .subscribe(b => resolve(b));
    });
  }
}

export interface IGetOrgTelemetryResponse {
  identifier: string,
  assetId: number,
  assetType: number,
  assetTypeTitle?: string,
  purposeId: number,
  gatewayId: string,
  gatewayOnline: 'online' | 'offline' | string,
  gatewayTitle: string,
  max: number,
  min: number,
  rag: 'red' | 'amber' | 'green' | undefined,
  siteId: number,
  siteTitle: string,
  title: string,
  updatedAt: Date,
  value: number,
  setting?: string;
  formattedValue?: string
}
